import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { createAppTheme, getStoredThemeMode, resolveInitialThemeMode, THEME_MODE_STORAGE_KEY } from '../theme';

const ThemeModeContext = createContext({
  mode: 'light',
  toggleThemeMode: () => {},
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

export default function OutreachThemeProvider({ children }) {
  const [mode, setMode] = useState(resolveInitialThemeMode);
  const [usingSystemPreference, setUsingSystemPreference] = useState(() => !getStoredThemeMode());

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      if (usingSystemPreference) {
        setMode(event.matches ? 'dark' : 'light');
      }
    };

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [usingSystemPreference]);

  const toggleThemeMode = () => {
    setMode((current) => {
      const nextMode = current === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, nextMode);
      return nextMode;
    });
    setUsingSystemPreference(false);
  };

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(
    () => ({
      mode,
      toggleThemeMode,
      usingSystemPreference,
    }),
    [mode, usingSystemPreference]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
